"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { Textarea } from "../components/ui/textarea"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import { useLanguage } from "../contexts/LanguageContext"
import { agentService } from "../services/api"

import { 
  MessageSquare, 
  FileText, 
  Image, 
  Code, 
  ArrowRight,
  Star,
  TrendingUp,
  Send
} from "lucide-react"

export default function HomePage() {
  const navigate = useNavigate()
  const { t } = useLanguage()


  const [prompt, setPrompt] = useState("")
  const [isCreatingSession, setIsCreatingSession] = useState(false)




  const handlePromptSubmit = async () => {
    if (prompt.trim()) {
      try {
        setIsCreatingSession(true)
        
        // 检查用户是否已登录
        const token = localStorage.getItem("token")
        if (!token) {
                  // 如果未登录，保存用户输入到localStorage并跳转到登录页面
        localStorage.setItem('pending_prompt', prompt.trim())
        navigate("/login")
        return
        }
        
        // 调用API创建新session
        console.log('开始创建session，参数:', {
          prompt: prompt.trim()
        })
        
        const session = await agentService.createNewSession({
          prompt: prompt.trim()
        })
        
        console.log('创建session成功:', session)
        
        // 创建成功后跳转到agent页面
        if (session && session.sessionId) {
          // 同时传递sessionId和用户输入，这样agent页面可以显示用户输入并自动发送
          navigate(`/agent?sessionId=${session.sessionId}&input=${encodeURIComponent(prompt.trim())}`)
        } else {
          throw new Error('创建session失败：未获取到sessionId')
        }
      } catch (error) {
        console.error('创建session失败:', error)
        // 如果创建失败，回退到原来的方式
        navigate(`/agent?input=${encodeURIComponent(prompt.trim())}`)
      } finally {
        setIsCreatingSession(false)
      }
    }
  }

  // AI creation templates
  const aiTemplates = [
    {
      id: "conversation",
      title: t('home.conversationType'),
      description: t('home.conversationDesc'),
      icon: MessageSquare,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      examples: t('home.conversationExamples').split(', ')
    },
    {
      id: "file-conversion",
      title: t('home.fileConversionType'),
      description: t('home.fileConversionDesc'),
      icon: FileText,
      color: "text-green-600",
      bgColor: "bg-green-50",
      examples: t('home.fileConversionExamples').split(', ')
    },
    {
      id: "image-generation",
      title: t('home.imageGenerationType'),
      description: t('home.imageGenerationDesc'),
      icon: Image,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      examples: t('home.imageGenerationExamples').split(', ')
    },
    {
      id: "code-assistant",
      title: t('home.codeAssistantType'),
      description: t('home.codeAssistantDesc'),
      icon: Code,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      examples: t('home.codeAssistantExamples').split(', ')
    }
  ]

  // Recent AI app trends (realistic data)
  const recentTrends = [
    {
      id: 1,
      name: t('apps.smartCustomerService.name'),
      creator: t('apps.smartCustomerService.creator'),
      category: t('apps.smartCustomerService.category'),
      usage: 15420,
      rating: 4.8,
      trend: "up",
      trendValue: 156,
      description: t('apps.smartCustomerService.description')
    },
    {
      id: 2,
      name: t('apps.documentAnalysis.name'),
      creator: t('apps.documentAnalysis.creator'),
      category: t('apps.documentAnalysis.category'),
      usage: 8920,
      rating: 4.7,
      trend: "up",
      trendValue: 234,
      description: t('apps.documentAnalysis.description')
    },
    {
      id: 3,
      name: t('apps.aiDesigner.name'),
      creator: t('apps.aiDesigner.creator'),
      category: t('apps.aiDesigner.category'),
      usage: 21560,
      rating: 4.9,
      trend: "up",
      trendValue: 445,
      description: t('apps.aiDesigner.description')
    },
    {
      id: 4,
      name: t('apps.codeOptimizer.name'),
      creator: t('apps.codeOptimizer.creator'),
      category: t('apps.codeOptimizer.category'),
      usage: 12340,
      rating: 4.8,
      trend: "up",
      trendValue: 189,
      description: t('apps.codeOptimizer.description')
    },
    {
      id: 5,
      name: t('apps.multilingualTranslator.name'),
      creator: t('apps.multilingualTranslator.creator'),
      category: t('apps.multilingualTranslator.category'),
      usage: 18760,
      rating: 4.6,
      trend: "up",
      trendValue: 267,
      description: t('apps.multilingualTranslator.description')
    },
    {
      id: 6,
      name: t('apps.financialAnalysis.name'),
      creator: t('apps.financialAnalysis.creator'),
      category: t('apps.financialAnalysis.category'),
      usage: 9650,
      rating: 4.7,
      trend: "up",
      trendValue: 178,
      description: t('apps.financialAnalysis.description')
    },
    {
      id: 7,
      name: t('apps.videoGenerator.name'),
      creator: t('apps.videoGenerator.creator'),
      category: t('apps.videoGenerator.category'),
      usage: 13420,
      rating: 4.5,
      trend: "up",
      trendValue: 312,
      description: t('apps.videoGenerator.description')
    },
    {
      id: 8,
      name: t('apps.legalAssistant.name'),
      creator: t('apps.legalAssistant.creator'),
      category: t('apps.legalAssistant.category'),
      usage: 6780,
      rating: 4.8,
      trend: "up",
      trendValue: 145,
      description: t('apps.legalAssistant.description')
    },
    {
      id: 9,
      name: t('apps.medicalDiagnosis.name'),
      creator: t('apps.medicalDiagnosis.creator'),
      category: t('apps.medicalDiagnosis.category'),
      usage: 11230,
      rating: 4.9,
      trend: "up",
      trendValue: 289,
      description: t('apps.medicalDiagnosis.description')
    },
    {
      id: 10,
      name: t('apps.educationTutor.name'),
      creator: t('apps.educationTutor.creator'),
      category: t('apps.educationTutor.category'),
      usage: 15680,
      rating: 4.7,
      trend: "up",
      trendValue: 223,
      description: t('apps.educationTutor.description')
    },
    {
      id: 11,
      name: t('apps.marketingCopy.name'),
      creator: t('apps.marketingCopy.creator'),
      category: t('apps.marketingCopy.category'),
      usage: 8920,
      rating: 4.6,
      trend: "up",
      trendValue: 167,
      description: t('apps.marketingCopy.description')
    },
    {
      id: 12,
      name: t('apps.3dModelGenerator.name'),
      creator: t('apps.3dModelGenerator.creator'),
      category: t('apps.3dModelGenerator.category'),
      usage: 7430,
      rating: 4.5,
      trend: "up",
      trendValue: 134,
      description: t('apps.3dModelGenerator.description')
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />

      {/* Module 1: Platform Slogan with Prompt Input */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('home.buildYourOwnAI')}
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('home.promptSubtitle')}
            </p>
            
            {/* Prompt Input */}
            <div className="max-w-3xl mx-auto mb-8">
              <div className="relative">
                <Textarea
                  placeholder={t('home.promptPlaceholder')}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="w-full text-lg py-4 pr-16 min-h-[120px] text-base border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl shadow-sm resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      e.preventDefault()
                      handlePromptSubmit()
                    }
                  }}
                  rows={5}
                />
                <Button 
                  onClick={handlePromptSubmit}
                  size="sm"
                  className="absolute right-3 bottom-3 bg-blue-800 hover:bg-blue-900 h-10 w-10 p-0 rounded-lg shadow-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!prompt.trim() || isCreatingSession}
                >
                  {isCreatingSession ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
                

              </div>
            </div>


          </div>
        </div>
      </section>

      {/* Module 2: AI Creation Templates */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('home.aiTemplatesTitle')}</h2>
            <p className="text-lg text-gray-600">{t('home.aiTemplatesSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiTemplates.map((template) => (
              <Card 
                key={template.id} 
                className="hover:shadow-lg transition-shadow"
              >
                <CardHeader>
                  <div className={`${template.bgColor} p-3 rounded-lg w-fit mb-3`}>
                    <template.icon className={`h-6 w-6 ${template.color}`} />
                  </div>
                  <CardTitle className="text-lg">{template.title}</CardTitle>
                  <CardDescription className="text-sm">
                    {template.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600">{t('home.appExamples')}</p>
                    <div className="flex flex-wrap gap-1">
                      {template.examples.map((example, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {example}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Module 3: Recent AI App Trends */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('home.trendsTitle')}</h2>
            <p className="text-lg text-gray-600">{t('home.trendsSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {recentTrends.slice(0, 8).map((trend) => (
              <Card key={trend.id} className="hover:shadow-lg transition-all duration-200 hover:scale-105">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1 line-clamp-1">{trend.name}</CardTitle>
                      <CardDescription className="text-sm">
                        {t('home.createdBy')} {trend.creator} {t('home.created')}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-medium">{trend.rating}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {trend.description}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{t('home.type')}</span>
                      <Badge variant="secondary" className="text-xs">{trend.category}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{t('home.usageCount')}</span>
                      <span className="font-medium">{trend.usage.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{t('home.growthTrend')}</span>
                      <div className="flex items-center gap-1 text-green-600">
                        <TrendingUp className="h-3 w-3" />
                        <span className="font-medium">+{trend.trendValue}%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          

        </div>
      </section>

      <Footer />
    </div>
  )
}
